import React, { useState, useEffect, useRef } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { templateService } from '../services/api';
import { toast } from 'react-hot-toast';
import { useLanguage } from '../contexts/LanguageContext';
import templateTemplates from '../data/metaTemplateTemplates.json';
import { templateVariables, getVariablesByCategory } from '../data/templateVariables';

const MetaTemplateCreateDialog = ({ isOpen, onClose, onSuccess }) => {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [showTemplateSelection, setShowTemplateSelection] = useState(true);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const headerTextareaRef = useRef(null);
  const bodyTextareaRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    category: 'UTILITY',
    language: 'en_US',
    description: '',
    parameterFormat: 'positional', // 'named' or 'positional'
    tags: [],
    tagInput: '',
    locked: {
      name: false,
      category: false,
      language: false,
      description: false,
      parameterFormat: false
    },
    header: { enabled: false, text: '', format: 'TEXT', examples: {}, variableTypes: {}, locked: { enabled: false, text: false, format: false, examples: false, variableTypes: false } },
    body: { text: '', examples: {}, variableTypes: {}, locked: { text: false, examples: false, variableTypes: false } },
    footer: { enabled: false, text: '', locked: { enabled: false, text: false } },
    buttons: []
  });

  // Reset form when dialog opens
  useEffect(() => {
    if (isOpen) {
      setShowTemplateSelection(true);
      setSelectedTemplateId(null);
      setFormData({
        name: '',
        category: 'UTILITY',
        language: 'en_US',
        description: '',
        parameterFormat: 'positional',
        tags: [],
        tagInput: '',
        locked: {
          name: false,
          category: false,
          language: false,
          description: false,
          parameterFormat: false
        },
        header: { enabled: false, text: '', format: 'TEXT', examples: {}, variableTypes: {}, locked: { enabled: false, text: false, format: false, examples: false, variableTypes: false } },
        body: { text: '', examples: {}, variableTypes: {}, locked: { text: false, examples: false, variableTypes: false } },
        footer: { enabled: false, text: '', locked: { enabled: false, text: false } },
        buttons: []
      });
    }
  }, [isOpen]);

  const languages = [
    { code: 'en_US', name: 'English' },
    { code: 'zh_HK', name: 'Chinese (Hong Kong)' },
    // { code: 'zh_CN', name: 'Chinese (Simplified)' },
    // { code: 'zh_TW', name: 'Chinese (Traditional)' },
  ];

  // Helper function to parse Meta API error message
  const parseMetaError = (errorMessage) => {
    if (!errorMessage) return null;

    // Try to extract the "Details" part from the error message
    const detailsMatch = errorMessage.match(/Details:\s*(.+?)(?:\n|$)/i);
    if (detailsMatch) {
      return detailsMatch[1].trim();
    }

    // Try to extract the "Title" part
    const titleMatch = errorMessage.match(/Title:\s*(.+?)(?:\n|$)/i);
    if (titleMatch) {
      return titleMatch[1].trim();
    }

    // Return the full message if no pattern matches
    return errorMessage;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error('Template name is required');
      return;
    }

    // Validate template name format (lowercase letters and underscores only)
    const namePattern = /^[a-z_]+$/;
    if (!namePattern.test(formData.name.trim())) {
      toast.error('Template name can only contain lowercase letters and underscores (e.g., appointment_reminder)');
      return;
    }

    if (!formData.body.text.trim()) {
      toast.error('Body text is required');
      return;
    }

    // Validate that examples are provided for all variables
    const bodyVariables = extractVariables(formData.body.text);
    const missingBodyExamples = bodyVariables.filter(v => !formData.body.examples[v] || !formData.body.examples[v].trim());

    if (missingBodyExamples.length > 0) {
      toast.error(`Please provide example values for all variables: ${missingBodyExamples.join(', ')}`);
      return;
    }

    if (formData.header.enabled && formData.header.text) {
      const headerVariables = extractVariables(formData.header.text);
      const missingHeaderExamples = headerVariables.filter(v => !formData.header.examples[v] || !formData.header.examples[v].trim());

      if (missingHeaderExamples.length > 0) {
        toast.error(`Please provide example values for header variables: ${missingHeaderExamples.join(', ')}`);
        return;
      }
    }

    setLoading(true);
    try {
      // Build components array
      const components = [];
      const isNamedFormat = formData.parameterFormat === 'named';

      // Helper function to convert text with variables to the correct format
      const convertVariableFormat = (text, variables) => {
        if (isNamedFormat) {
          // For named format, keep variable names as-is: {{variable_name}}
          return text;
        } else {
          // For positional format, convert to {{1}}, {{2}}, etc.
          let convertedText = text;
          variables.forEach((varName, index) => {
            const position = index + 1;
            const regex = new RegExp(`\\{\\{${varName}\\}\\}`, 'g');
            convertedText = convertedText.replace(regex, `{{${position}}}`);
          });
          return convertedText;
        }
      };

      // Helper function to format examples according to Meta's requirements
      const formatExamples = (variables, examples, componentType) => {
        if (variables.length === 0) return null;

        if (isNamedFormat) {
          // Named format: body_text_named_params array
          if (componentType === 'BODY') {
            return {
              body_text_named_params: variables.map(varName => ({
                param_name: varName,
                example: examples[varName] || ''
              }))
            };
          } else if (componentType === 'HEADER') {
            // Header uses header_text array for named format
            return {
              header_text: variables.map(varName => examples[varName] || '')
            };
          }
        } else {
          // Positional format: body_text array of arrays
          if (componentType === 'BODY') {
            return {
              body_text: [variables.map(varName => examples[varName] || '')]
            };
          } else if (componentType === 'HEADER') {
            return {
              header_text: variables.map(varName => examples[varName] || '')
            };
          }
        }
        return null;
      };

      // Header component (optional)
      if (formData.header.enabled && formData.header.text) {
        const headerVariables = extractVariables(formData.header.text);
        const headerComponent = {
          type: 'HEADER',
          format: formData.header.format,
          text: convertVariableFormat(formData.header.text, headerVariables)
        };

        // Add example if there are variables
        if (headerVariables.length > 0) {
          const example = formatExamples(headerVariables, formData.header.examples, 'HEADER');
          if (example) {
            headerComponent.example = example;
          }
        }

        components.push(headerComponent);
      }

      // Body component (required)
      const bodyVariables = extractVariables(formData.body.text);
      const bodyComponent = {
        type: 'BODY',
        text: convertVariableFormat(formData.body.text, bodyVariables)
      };

      // Add example if there are variables
      if (bodyVariables.length > 0) {
        const example = formatExamples(bodyVariables, formData.body.examples, 'BODY');
        if (example) {
          bodyComponent.example = example;
        }
      }

      components.push(bodyComponent);

      // Footer component (optional)
      if (formData.footer.enabled && formData.footer.text) {
        components.push({
          type: 'FOOTER',
          text: formData.footer.text
        });
      }

      // Buttons (optional, up to 10)
      if (formData.buttons.length > 0) {
        const buttonComponent = {
          type: 'BUTTONS',
          buttons: formData.buttons.map(btn => ({
            type: btn.type,
            text: btn.text,
            ...(btn.type === 'URL' && btn.url ? { url: btn.url } : {}),
            ...(btn.type === 'PHONE_NUMBER' && btn.phone_number ? { phone_number: btn.phone_number } : {})
          }))
        };
        components.push(buttonComponent);
      }

      const templateData = {
        name: formData.name,
        category: formData.category,
        language: formData.language,
        components: components,
        parameter_format: formData.parameterFormat,
        description: formData.description,
        tags: formData.tags && formData.tags.length > 0 ? formData.tags : undefined
      };

      const response = await templateService.createMetaTemplate(templateData);

      if (response.success) {
        toast.success('Template created successfully! It is now pending approval from Meta.');
        onSuccess();
      } else {
        // Parse and display error message
        let errorMsg = response.message || response.error || 'Failed to create template';

        // Handle arrays or objects in error messages
        if (Array.isArray(errorMsg)) {
          errorMsg = errorMsg
            .map(err => {
              if (typeof err === 'object' && err.msg) {
                const loc = Array.isArray(err.loc) ? err.loc.slice(1).join('.') : '';
                return loc ? `${loc}: ${err.msg}` : err.msg;
              }
              return typeof err === 'string' ? err : JSON.stringify(err);
            })
            .join('; ');
        } else if (typeof errorMsg !== 'string') {
          errorMsg = JSON.stringify(errorMsg);
        }

        const parsedError = parseMetaError(errorMsg);
        toast.error(parsedError || errorMsg);
      }
    } catch (error) {
      console.error('Error creating template:', error);

      // Parse Meta API error response
      let errorMessage = 'Failed to create template';

      if (error.response?.data) {
        // Check for error field (Meta API format)
        if (error.response.data.error) {
          const parsedError = parseMetaError(error.response.data.error);
          errorMessage = parsedError || error.response.data.error;
        }
        // Check for detail field (standard API format)
        else if (error.response.data.detail) {
          // Handle array of validation errors (FastAPI validation errors)
          if (Array.isArray(error.response.data.detail)) {
            errorMessage = error.response.data.detail
              .map(err => {
                // Handle validation error objects with type, loc, msg, input
                if (typeof err === 'object' && err.msg) {
                  const loc = Array.isArray(err.loc) ? err.loc.slice(1).join('.') : '';
                  return loc ? `${loc}: ${err.msg}` : err.msg;
                }
                // Handle string errors
                return typeof err === 'string' ? err : JSON.stringify(err);
              })
              .join('; ');
          } else if (typeof error.response.data.detail === 'string') {
            errorMessage = error.response.data.detail;
          } else {
            // Fallback for other detail types
            errorMessage = JSON.stringify(error.response.data.detail);
          }
        }
        // Check for message field
        else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const addButton = () => {
    if (formData.buttons.length >= 10) {
      toast.error('Maximum 10 buttons allowed');
      return;
    }
    setFormData(prev => ({
      ...prev,
      buttons: [...prev.buttons, {
        type: 'QUICK_REPLY',
        text: '',
        url: '',
        phone_number: '',
        locked: {
          text: false,
          url: false,
          type: false,
          phone_number: false
        }
      }]
    }));
  };

  const removeButton = (index) => {
    setFormData(prev => ({
      ...prev,
      buttons: prev.buttons.filter((_, i) => i !== index)
    }));
  };

  const updateButton = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      buttons: prev.buttons.map((btn, i) =>
        i === index ? { ...btn, [field]: value } : btn
      )
    }));
  };

  // Extract variables from text using regex
  const extractVariables = (text) => {
    if (!text) return [];
    const variablePattern = /\{\{([^}]+)\}\}/g;
    const matches = [];
    let match;
    while ((match = variablePattern.exec(text)) !== null) {
      matches.push(match[1]);
    }
    // Remove duplicates while preserving order
    return [...new Set(matches)];
  };

  // Insert variable at cursor position in textarea
  const insertVariableAtCursor = (textareaRef, variableKey, field) => {
    if (!textareaRef.current) return;

    const textarea = textareaRef.current;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const currentText = field === 'header' ? formData.header.text : formData.body.text;
    const variableText = `{{${variableKey}}}`;

    // Insert variable at cursor position
    const newText = currentText.substring(0, start) + variableText + currentText.substring(end);

    // Update form state directly
    updateTextWithExamples(field, newText);

    // Set cursor position after inserted variable (use setTimeout to ensure DOM is updated)
    setTimeout(() => {
      if (textareaRef.current) {
        const newCursorPos = start + variableText.length;
        textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
        textareaRef.current.focus();
      }
    }, 0);
  };

  // Update examples when text changes
  const updateTextWithExamples = (field, text) => {
    const variables = extractVariables(text);
    const currentExamples = formData[field].examples || {};
    const currentTypes = formData[field].variableTypes || {};

    // Keep existing examples and types, add empty ones for new variables
    const newExamples = {};
    const newTypes = {};
    variables.forEach(varName => {
      newExamples[varName] = currentExamples[varName] || '';
      // Default to 'text' (Name) if not set, or infer from variable name
      newTypes[varName] = currentTypes[varName] || (varName.match(/^\d+$/) ? 'numeric' : 'text');
    });

    setFormData(prev => ({
      ...prev,
      [field]: {
        ...prev[field],
        text: text,
        examples: newExamples,
        variableTypes: newTypes
      }
    }));
  };

  const updateExample = (field, variableName, exampleValue) => {
    setFormData(prev => ({
      ...prev,
      [field]: {
        ...prev[field],
        examples: {
          ...prev[field].examples,
          [variableName]: exampleValue
        }
      }
    }));
  };

  const updateVariableType = (field, variableName, type) => {
    setFormData(prev => ({
      ...prev,
      [field]: {
        ...prev[field],
        variableTypes: {
          ...prev[field].variableTypes,
          [variableName]: type
        }
      }
    }));
  };

  // VariableSelector Component
  const VariableSelector = ({ textareaRef, field }) => {
    const variablesByCategory = getVariablesByCategory();
    const categories = Object.keys(variablesByCategory);

    // Initialize all categories as expanded by default
    const [expandedCategories, setExpandedCategories] = useState(() => {
      const initial = {};
      Object.keys(variablesByCategory).forEach(category => {
        initial[category] = true;
      });
      return initial;
    });

    const toggleCategory = (category) => {
      setExpandedCategories(prev => ({
        ...prev,
        [category]: !prev[category]
      }));
    };

    const handleVariableClick = (variableKey) => {
      insertVariableAtCursor(textareaRef, variableKey, field);
      // Keep categories expanded after selection - don't change expandedCategories state
    };

    return (
      <div className="mb-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
        <p className="text-sm font-medium text-gray-700 mb-2">
          {t('metaTemplates.availableVariables', 'Available Variables')}
        </p>
        <p className="text-xs text-gray-500 mb-3">
          {t('metaTemplates.clickToInsert', 'Click a variable to insert it at cursor position')}
        </p>
        <div className="space-y-2">
          {categories.map(category => (
            <div key={category} className="border border-gray-200 rounded">
              <button
                type="button"
                onClick={() => toggleCategory(category)}
                className="w-full px-3 py-2 text-left text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 flex items-center justify-between rounded-t"
              >
                <span>{category}</span>
                <span className="text-gray-500">
                  {expandedCategories[category] ? '−' : '+'}
                </span>
              </button>
              {expandedCategories[category] && (
                <div className="p-2 bg-white rounded-b">
                  <div className="flex flex-wrap gap-2">
                    {variablesByCategory[category].map(variable => (
                      <button
                        key={variable.key}
                        type="button"
                        onClick={() => handleVariableClick(variable.key)}
                        className="px-3 py-1.5 text-xs font-mono bg-blue-50 text-blue-700 border border-blue-200 rounded hover:bg-blue-100 hover:border-blue-300 transition-colors"
                        title={`${variable.displayName}: ${variable.description} (Example: ${variable.example})`}
                      >
                        {`{{${variable.key}}}`}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Load template into form
  const loadTemplate = (template) => {
    setFormData({
      name: template.name,
      category: template.category,
      language: template.language,
      description: template.description || '',
      parameterFormat: template.parameterFormat || 'positional',
      tags: template.tags || [],
      locked: {
        name: template.locked?.name === true,
        category: template.locked?.category === true,
        language: template.locked?.language === true,
        description: template.locked?.description === true,
        parameterFormat: template.locked?.parameterFormat === true
      },
      header: {
        enabled: template.header.enabled || false,
        text: template.header.text || '',
        format: template.header.format || 'TEXT',
        examples: template.header.examples || {},
        variableTypes: template.header.variableTypes || {},
        locked: {
          enabled: template.header.locked?.enabled === true,
          text: template.header.locked?.text === true,
          format: template.header.locked?.format === true,
          examples: template.header.locked?.examples === true,
          variableTypes: template.header.locked?.variableTypes === true
        }
      },
      body: {
        text: template.body.text || '',
        examples: template.body.examples || {},
        variableTypes: template.body.variableTypes || {},
        locked: {
          text: template.body.locked?.text === true,
          examples: template.body.locked?.examples === true,
          variableTypes: template.body.locked?.variableTypes === true
        }
      },
      footer: {
        enabled: template.footer.enabled || false,
        text: template.footer.text || '',
        locked: {
          enabled: template.footer.locked?.enabled === true,
          text: template.footer.locked?.text === true
        }
      },
      buttons: (template.buttons || []).map(btn => ({
        ...btn,
        locked: {
          text: btn.locked?.text === true,
          url: btn.locked?.url === true,
          type: btn.locked?.type === true,
          phone_number: btn.locked?.phone_number === true
        }
      }))
    });
    setShowTemplateSelection(false);
    setSelectedTemplateId(template.id);
  };

  // Start from scratch
  const startFromScratch = () => {
    setFormData({
      name: '',
      category: 'UTILITY',
      language: 'en_US',
      description: '',
      parameterFormat: 'positional',
      tags: [],
      tagInput: '',
      locked: {
        name: false,
        category: false,
        language: false,
        description: false,
        parameterFormat: false
      },
      header: { enabled: false, text: '', format: 'TEXT', examples: {}, variableTypes: {}, locked: { enabled: false, text: false, format: false, examples: false, variableTypes: false } },
      body: { text: '', examples: {}, variableTypes: {}, locked: { text: false, examples: false, variableTypes: false } },
      footer: { enabled: false, text: '', locked: { enabled: false, text: false } },
      buttons: []
    });
    setShowTemplateSelection(false);
    setSelectedTemplateId(null);
  };

  // Handle ESC key press
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEsc);
      return () => document.removeEventListener('keydown', handleEsc);
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Template Selection View
  if (showTemplateSelection) {
    return (
      <div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            onClose();
          }
        }}
      >
        <div
          className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">
              {t('metaTemplates.selectTemplate', 'Select a Template')}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Template List */}
          <div className="flex-1 overflow-y-auto p-6">
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-4">
                {t('metaTemplates.selectTemplateDescription', 'Choose a pre-designed template to get started, or create your own from scratch.')}
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {templateTemplates.map((template) => (
                <div
                  key={template.id}
                  onClick={() => loadTemplate(template)}
                  className="border border-gray-200 rounded-lg p-4 hover:border-blue-500 hover:shadow-md cursor-pointer transition-all"
                >
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-semibold text-gray-900">{template.display_name}</h3>
                    <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded">
                      {template.category}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-3">{template.description}</p>
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <span>{template.language === 'en_US' ? 'English' : 'Chinese (HK)'}</span>
                    <span>•</span>
                    <span>{template.parameterFormat === 'positional' ? 'Positional' : 'Named'}</span>
                    {template.header.enabled && <><span>•</span><span>Header</span></>}
                    {template.footer.enabled && <><span>•</span><span>Footer</span></>}
                    {template.buttons.length > 0 && <><span>•</span><span>{template.buttons.length} Button{template.buttons.length > 1 ? 's' : ''}</span></>}
                  </div>
                  {template.body.text && (
                    <div className="mt-3 p-2 bg-gray-50 rounded text-xs text-gray-700 line-clamp-2">
                      {template.body.text.substring(0, 100)}{template.body.text.length > 100 ? '...' : ''}
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="border-t border-gray-200 pt-4">
              <button
                onClick={startFromScratch}
                className="w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-700 hover:border-blue-500 hover:text-blue-600 transition-all"
              >
                {t('metaTemplates.startFromScratch', 'Start from Scratch')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            {selectedTemplateId && (
              <button
                onClick={() => setShowTemplateSelection(true)}
                className="text-gray-500 hover:text-gray-700 text-sm"
              >
                ← {t('metaTemplates.back', 'Back')}
              </button>
            )}
            <h2 className="text-xl font-semibold text-gray-900">
              {t('metaTemplates.createTemplate', 'Create Template')}
              {selectedTemplateId && (
                <span className="ml-2 text-sm font-normal text-gray-500">
                  ({templateTemplates.find(t => t.id === selectedTemplateId)?.display_name || templateTemplates.find(t => t.id === selectedTemplateId)?.name})
                </span>
              )}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            {/* Template Name, Category, and Language - Horizontal Layout */}
            <div className="grid grid-cols-3 gap-4">
              {/* Template Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('metaTemplates.templateName', 'Template Name')} *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => {
                    // Convert to lowercase and replace spaces/special chars with underscores
                    const value = e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_');
                    setFormData(prev => ({ ...prev, name: value }));
                  }}
                  disabled={formData.locked.name}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  placeholder="appointment_reminder"
                  maxLength={512}
                />
                <p className="mt-1 text-sm text-gray-500">
                  {formData.name.length}/512
                  {formData.name && !/^[a-z_]+$/.test(formData.name) && (
                    <span className="ml-2 text-red-600">Only lowercase letters and underscores allowed</span>
                  )}
                </p>
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('metaTemplates.category', 'Category')} *
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                  disabled={formData.locked.category}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  <option value="UTILITY">Utility</option>
                  <option value="AUTHENTICATION">Authentication</option>
                  <option value="MARKETING">Marketing</option>
                </select>
              </div>

              {/* Language */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('metaTemplates.language', 'Language')} *
                </label>
                <select
                  value={formData.language}
                  onChange={(e) => setFormData(prev => ({ ...prev, language: e.target.value }))}
                  disabled={formData.locked.language}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  <option value="">Language...</option>
                  {languages.map(lang => (
                    <option key={lang.code} value={lang.code}>{lang.name}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('metaTemplates.descriptionField', 'Description')} (Optional)
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                disabled={formData.locked.description}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                rows={2}
                placeholder="Enter template description"
              />
            </div>

            {/* Tags */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('metaTemplates.tags', 'Tags')} (Optional)
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={formData.tagInput || ''}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, tagInput: e.target.value }));
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && formData.tagInput && formData.tagInput.trim()) {
                      e.preventDefault();
                      const newTag = formData.tagInput.trim();
                      if (!formData.tags.includes(newTag)) {
                        setFormData(prev => ({
                          ...prev,
                          tags: [...prev.tags, newTag],
                          tagInput: ''
                        }));
                      } else {
                        // Tag already exists, just clear input
                        setFormData(prev => ({ ...prev, tagInput: '' }));
                      }
                    }
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter a tag and press Enter"
                />
                <button
                  type="button"
                  onClick={() => {
                    if (formData.tagInput && formData.tagInput.trim()) {
                      const newTag = formData.tagInput.trim();
                      if (!formData.tags.includes(newTag)) {
                        setFormData(prev => ({
                          ...prev,
                          tags: [...prev.tags, newTag],
                          tagInput: ''
                        }));
                      } else {
                        // Tag already exists, just clear input
                        setFormData(prev => ({ ...prev, tagInput: '' }));
                      }
                    }
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {t('metaTemplates.addTag', 'Add')}
                </button>
              </div>
              <p className="mt-1 text-sm text-gray-500">
                {t('metaTemplates.tagsHelp', 'Enter a tag and press Enter or click Add to add it')}
              </p>
              {formData.tags.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {formData.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => {
                          setFormData(prev => ({
                            ...prev,
                            tags: prev.tags.filter((_, i) => i !== index)
                          }));
                        }}
                        className="ml-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full hover:bg-blue-200"
                        title={t('metaTemplates.removeTag', 'Remove tag')}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Parameter Format */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('metaTemplates.parameterFormat', 'Parameter Format')}
              </label>
              <select
                value={formData.parameterFormat}
                onChange={(e) => setFormData(prev => ({ ...prev, parameterFormat: e.target.value }))}
                disabled={formData.locked.parameterFormat}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                <option value="positional">{t('metaTemplates.positionalFormat', 'Positional ({{1}}, {{2}}, ...)')}</option>
                <option value="named">{t('metaTemplates.namedFormat', 'Named ({{variable_name}})')}</option>
              </select>
              <p className="mt-1 text-sm text-gray-500">
                {formData.parameterFormat === 'positional'
                  ? t('metaTemplates.positionalFormatHelp', 'Variables will be converted to {{1}}, {{2}}, etc. in order.')
                  : t('metaTemplates.namedFormatHelp', 'Variables will keep their names: {{variable_name}}')
                }
              </p>
            </div>

            {/* Header */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  {t('metaTemplates.header', 'Header')} (Optional)
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.header.enabled}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      header: { ...prev.header, enabled: e.target.checked }
                    }))}
                    disabled={formData.header.locked.enabled}
                    className="mr-2 disabled:cursor-not-allowed"
                  />
                  <span className="text-sm text-gray-600">Enable Header</span>
                </label>
              </div>
              {formData.header.enabled && (
                <>
                  <VariableSelector textareaRef={headerTextareaRef} field="header" />
                  <textarea
                    ref={headerTextareaRef}
                    value={formData.header.text}
                    onChange={(e) => updateTextWithExamples('header', e.target.value)}
                    disabled={formData.header.locked.text}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    rows={2}
                    placeholder="Add header text. Use {{variable}} for variables."
                    maxLength={60}
                  />
                  {extractVariables(formData.header.text).length > 0 && (
                    <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-sm font-medium text-blue-900 mb-2">
                        {t('metaTemplates.variableExamples', 'Variable Examples')} (Required)
                      </p>
                      {extractVariables(formData.header.text).map((varName, index) => (
                        <div key={index} className="mb-3">
                          <div className="flex items-center justify-between mb-1">
                            <label className="block text-sm text-blue-800 font-medium">
                              {`{{${varName}}}`}
                            </label>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-blue-700">{t('metaTemplates.typeOfVariable', 'Type of variable')}:</span>
                              <div className="flex gap-2">
                                <label className="flex items-center cursor-pointer">
                                  <input
                                    type="radio"
                                    name={`header-type-${varName}`}
                                    value="text"
                                    checked={formData.header.variableTypes[varName] === 'text'}
                                    onChange={(e) => updateVariableType('header', varName, e.target.value)}
                                    disabled={formData.header.locked.variableTypes}
                                    className="mr-1 disabled:cursor-not-allowed"
                                  />
                                  <span className="text-xs text-blue-800">{t('metaTemplates.variableTypeName', 'Name')}</span>
                                </label>
                                <label className="flex items-center cursor-pointer">
                                  <input
                                    type="radio"
                                    name={`header-type-${varName}`}
                                    value="numeric"
                                    checked={formData.header.variableTypes[varName] === 'numeric'}
                                    onChange={(e) => updateVariableType('header', varName, e.target.value)}
                                    disabled={formData.header.locked.variableTypes}
                                    className="mr-1 disabled:cursor-not-allowed"
                                  />
                                  <span className="text-xs text-blue-800">{t('metaTemplates.variableTypeNumber', 'Number')}</span>
                                </label>
                              </div>
                            </div>
                          </div>
                          <input
                            type="text"
                            value={formData.header.examples[varName] || ''}
                            onChange={(e) => updateExample('header', varName, e.target.value)}
                            disabled={formData.header.locked.examples}
                            className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                            placeholder={`Example value for ${varName}`}
                            required
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Body */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('metaTemplates.body', 'Body')} *
              </label>
              <VariableSelector textareaRef={bodyTextareaRef} field="body" />
              <textarea
                ref={bodyTextareaRef}
                value={formData.body.text}
                onChange={(e) => updateTextWithExamples('body', e.target.value)}
                disabled={formData.body.locked.text}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                rows={6}
                placeholder="Enter message body. Use {{variable}} for variables."
                maxLength={1024}
              />
              <p className="mt-1 text-sm text-gray-500">
                {formData.body.text.length}/1024
              </p>
              <p className="mt-1 text-sm text-gray-500">
                {formData.parameterFormat === 'positional'
                  ? t('metaTemplates.variableHintPositional', 'Use {{variable_name}} to add variables. They will be converted to {{1}}, {{2}}, etc.')
                  : t('metaTemplates.variableHintNamed', 'Use {{variable_name}} to add variables. Variable names must be lowercase with underscores.')
                }
              </p>
              {extractVariables(formData.body.text).length > 0 && (
                <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm font-medium text-blue-900 mb-2">
                    {t('metaTemplates.variableExamples', 'Variable Examples')} (Required)
                  </p>
                  <p className="text-xs text-blue-700 mb-3">
                    {t('metaTemplates.variableExamplesNote', 'Provide example values for each variable. These examples will be shown to Meta reviewers.')}
                  </p>
                  {extractVariables(formData.body.text).map((varName, index) => (
                    <div key={index} className="mb-3">
                      <div className="flex items-center justify-between mb-1">
                        <label className="block text-sm text-blue-800 font-medium">
                          {`{{${varName}}}`}
                        </label>
                        {/* <div className="flex items-center gap-2">
                          <span className="text-xs text-blue-700">{t('metaTemplates.typeOfVariable', 'Type of variable')}:</span>
                          <div className="flex gap-2">
                            <label className="flex items-center cursor-pointer">
                              <input
                                type="radio"
                                name={`body-type-${varName}`}
                                value="text"
                                checked={formData.body.variableTypes[varName] === 'text'}
                                onChange={(e) => updateVariableType('body', varName, e.target.value)}
                                className="mr-1"
                              />
                              <span className="text-xs text-blue-800">{t('metaTemplates.variableTypeName', 'Name')}</span>
                            </label>
                            <label className="flex items-center cursor-pointer">
                              <input
                                type="radio"
                                name={`body-type-${varName}`}
                                value="numeric"
                                checked={formData.body.variableTypes[varName] === 'numeric'}
                                onChange={(e) => updateVariableType('body', varName, e.target.value)}
                                className="mr-1"
                              />
                              <span className="text-xs text-blue-800">{t('metaTemplates.variableTypeNumber', 'Number')}</span>
                            </label>
                          </div>
                        </div> */}
                      </div>
                      <input
                        type="text"
                        value={formData.body.examples[varName] || ''}
                        onChange={(e) => updateExample('body', varName, e.target.value)}
                        disabled={formData.body.locked.examples}
                        className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                        placeholder={`Example value for ${varName}`}
                        required
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  {t('metaTemplates.footer', 'Footer')} (Optional)
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.footer.enabled}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      footer: { ...prev.footer, enabled: e.target.checked }
                    }))}
                    disabled={formData.footer.locked.enabled}
                    className="mr-2 disabled:cursor-not-allowed"
                  />
                  <span className="text-sm text-gray-600">Enable Footer</span>
                </label>
              </div>
              {formData.footer.enabled && (
                <input
                  type="text"
                  value={formData.footer.text}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    footer: { ...prev.footer, text: e.target.value }
                  }))}
                  disabled={formData.footer.locked.text}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  placeholder="Enter footer text"
                  maxLength={60}
                />
              )}
            </div>

            {/* Buttons */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  {t('metaTemplates.buttons', 'Buttons')} (Optional)
                </label>
                <button
                  type="button"
                  onClick={addButton}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  + Add Button
                </button>
              </div>
              {formData.buttons.map((button, index) => (
                <div key={index} className="mb-3 p-3 border border-gray-200 rounded-lg">
                  <div className="grid grid-cols-2 gap-3 mb-2">
                    <select
                      value={button.type}
                      onChange={(e) => updateButton(index, 'type', e.target.value)}
                      disabled={button.locked?.type}
                      className="px-3 py-2 border border-gray-300 rounded-lg disabled:bg-gray-100 disabled:cursor-not-allowed"
                    >
                      <option value="QUICK_REPLY">Quick Reply</option>
                      <option value="URL">URL</option>
                      <option value="PHONE_NUMBER">Phone Number</option>
                    </select>
                    <button
                      type="button"
                      onClick={() => removeButton(index)}
                      className="text-red-600 hover:text-red-800 text-sm"
                    >
                      Remove
                    </button>
                  </div>
                  <input
                    type="text"
                    value={button.text}
                    onChange={(e) => updateButton(index, 'text', e.target.value)}
                    disabled={button.locked?.text}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-2 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    placeholder="Button text"
                  />
                  {button.type === 'URL' && (
                    <input
                      type="text"
                      value={button.url}
                      onChange={(e) => updateButton(index, 'url', e.target.value)}
                      disabled={button.locked?.url}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg disabled:bg-gray-100 disabled:cursor-not-allowed"
                      placeholder="URL"
                    />
                  )}
                  {button.type === 'PHONE_NUMBER' && (
                    <input
                      type="text"
                      value={button.phone_number}
                      onChange={(e) => updateButton(index, 'phone_number', e.target.value)}
                      disabled={button.locked?.phone_number}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg disabled:bg-gray-100 disabled:cursor-not-allowed"
                      placeholder="Phone number"
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            {t('common.cancel', 'Cancel')}
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={loading}
            className="px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? t('common.submitting', 'Submitting...') : t('common.submit', 'Submit for Review')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MetaTemplateCreateDialog;

